import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';

import { createEntitySlice, serializeAxiosError, EntityState } from '../../shared/reducers/reducer.utils';
import { cleanEntity } from '../../shared/util/entity-utils/entity-utils';

const initialState: EntityState<any> = {
    loading: false,
    errorMessage: null,
    entities: [],
    empresas: [],
    entity: {},
    updating: false,
    totalItems: 0,
    updateSuccess: false,
};

const apiUrl = 'api/grupo-empresarials';

// Actions

export const getEntities = createAsyncThunk(
    'grupoEmpresarial/fetch_entity_list',
    async ({ page, size, sort, query }: any) => {
        const requestUrl = `${apiUrl}?${sort ? `page=${page}&size=${size}&sort=${sort}&` : ''}cacheBuster=${new Date().getTime()}&query=${query}`;
        return axios.get<any[]>(requestUrl);
    },
    { serializeError: serializeAxiosError },
);

export const getEmpresasByGrupo = createAsyncThunk(
    'grupoEmpresarial/empresas',
    async ({ id, page, size, sort }: any) => {
        const requestUrl = `${apiUrl}/${id}/empresa?${sort ? `page=${page}&size=${size}&sort=${sort}&` : ''}cacheBuster=${new Date().getTime()}`;
        return axios.get<any[]>(requestUrl);
    },
    { serializeError: serializeAxiosError },
);

export const getEntity = createAsyncThunk(
    'grupoEmpresarial/fetch_entity',
    async (id: string | number) => {
        const requestUrl = `${apiUrl}/${id}`;
        return axios.get<any>(requestUrl);
    },
    { serializeError: serializeAxiosError },
);

export const createEntity = createAsyncThunk(
    'grupoEmpresarial/create_entity',
    async (entity: any, thunkAPI) => {
        const result = await axios.post<any>(apiUrl, cleanEntity(entity));
        thunkAPI.dispatch(getEntities({}));
        return result;
    },
    { serializeError: serializeAxiosError },
);

export const updateEntity = createAsyncThunk(
    'grupoEmpresarial/update_entity',
    async (entity: any, thunkAPI) => {
        const result = await axios.put<any>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
        thunkAPI.dispatch(getEntities({}));
        return result;
    },
    { serializeError: serializeAxiosError },
);

export const partialUpdateEntity = createAsyncThunk(
    'grupoEmpresarial/partial_update_entity',
    async (entity: any, thunkAPI) => {
        const result = await axios.patch<any>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
        thunkAPI.dispatch(getEntities({}));
        return result;
    },
    { serializeError: serializeAxiosError },
);

export const deleteEntity = createAsyncThunk(
    'grupoEmpresarial/delete_entity',
    async (id: string | number, thunkAPI) => {
        const requestUrl = `${apiUrl}/${id}`;
        const result = await axios.delete<any>(requestUrl);
        thunkAPI.dispatch(getEntities({}));
        return result;
    },
    { serializeError: serializeAxiosError },
);

// slice

export const GrupoEmpresarialSlice = createEntitySlice({
    name: 'grupoEmpresarial',
    initialState,
    extraReducers(builder) {
        builder
            .addCase(getEntity.fulfilled, (state, action) => {
                state.loading = false;
                state.entity = action.payload.data;
            })
            .addCase(deleteEntity.fulfilled, state => {
                state.updating = false;
                state.updateSuccess = true;
                state.entity = {};
            })
            .addMatcher(isFulfilled(getEntities), (state, action) => {
                const { data, headers } = action.payload;
                return {
                    ...state,
                    loading: false,
                    entities: data,
                    totalItems: parseInt(headers['x-total-count'], 10),
                };
            })
            .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
                state.updating = false;
                state.loading = false;
                state.updateSuccess = true;
                state.entity = action.payload.data;
            })
            .addMatcher(isFulfilled(getEmpresasByGrupo), (state, action) => {
                state.updating = false;
                state.loading = false;
                state.updateSuccess = true;
                state.empresas = action.payload.data;
            })
            .addMatcher(isPending(getEntities, getEntity), state => {
                state.errorMessage = null;
                state.updateSuccess = false;
                state.loading = true;
            })
            .addMatcher(isPending(getEmpresasByGrupo, getEntity), state => {
                state.errorMessage = null;
                state.updateSuccess = false;
                state.loading = true;
                state.empresas = [];
            })

            .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
                state.errorMessage = null;
                state.updateSuccess = false;
                state.updating = true;
            });
    },
});

export const { reset } = GrupoEmpresarialSlice.actions;

// Reducer
export default GrupoEmpresarialSlice.reducer;
